import Link from "next/link";
import { useRouter } from "next/router";
import { useState, useEffect } from "react";
import { AnimationOnScroll } from "react-animation-on-scroll";
import Button from "../../app/component/commons/button/Button";
import { CardAuction } from "../../app/component/commons/card/Card";
import {
  ArrowRight2Icon,
  LocationCrossBoldIcon,
  TickBulkCircleIcon,
} from "../../app/component/commons/icon/SvgIcon";
import Image from "../../app/component/commons/image/Image";
import { LoadingPricingPlan } from "../../app/component/commons/loading/Skeleton";
import Meta from "../../app/component/commons/meta/Meta";
import { sleep } from "../../app/component/plugins/map/helper";
import Map from "../../app/component/plugins/map/Map";

import {
  pricing_1,
  pricing_2,
  what_you_got,
} from "../../app/helpers/ContentList";
import { formatMoney } from "../../app/helpers/formatMoney";
import DefaultLayout from "../../app/layouts/DefaultLayout";

const Index = ({}: {}) => {
  const router = useRouter();
  const [mode, setMode] = useState("");
  const [plan, setPlan] = useState("monthly");
  const [items, setItems] = useState<{
    status: string;
    data: {
      city: string;
      zipcode: string;
      found: {
        facilities: { lat: number; lng: number }[];
        upcoming_auction: number;
        avg_auction_monthly: number;
      };
      pricing: {
        monthly: number;
        yearly: number;
        yearly_save_value: number;
        yearly_save_percentage: number;
      };
    };
  }>({
    status: "",
    data: {
      city: "",
      zipcode: "",
      found: {
        facilities: [],
        upcoming_auction: 0,
        avg_auction_monthly: 0,
      },
      pricing: {
        monthly: 0,
        yearly: 0,
        yearly_save_value: 0,
        yearly_save_percentage: 0,
      },
    },
  });
  const [params, setParams] = useState<{
    lat: string;
    lng: string;
    radius: string | number;
    plan: string;
    map_zoom: string | number;
  }>({
    lat: "",
    lng: "",
    radius: "",
    plan: "",
    map_zoom: 11,
  });
  const [loading, setLoading] = useState(true);
  const [totalFac, setTotalFac] = useState(0);
  const [city, setCity] = useState("");

  useEffect(() => {
    if (window.innerWidth < 768) {
      setMode("mobile");
    } else {
      setMode("desktop");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Meta
        title="Pricing | Storage Unit Auction List"
        description="StorageUnitAuctionList.com charges different fees for its services, which you can explore by selecting a radius and then a subscription plan. All plans are offered on a monthly or yearly basis, and we accept all major credit cards."
      />
      <DefaultLayout topFooter="!mt-20">
        <div className="container mx-auto ">
          <section className="mt-12 md:mt-[5.75rem] flex flex-col items-center justify-center text-center px-4.5 md:px-0">
            <div className="text-warning-500 text-regular-tight md:text-large-none md:font-bold font-medium uppercase">
              Pricing
            </div>
            <div className="mt-4 md:mt-3.5 text-neutral-900 font-bold md:text-title1 md:leading-[3.625rem] text-title3 leading-[2.125rem]">
              {/* <span className="text-gradient-7">Plan</span> */}
              Select A Location & Subscription Type
            </div>
            <div className="mt-4 md:mt-3.5 text-neutral-700 text-large-normal leading-7 md:w-[30rem] hidden md:block">
              Join over 300,000 storage unit buyers who rely on us to hunt for
              storage unit auctions happening near them
            </div>
            <div className="mt-4.5 text-neutral-700 text-small-normal leading-6 md:w-[20.625rem] md:hidden px-2">
              Join over 300,000 storage unit buyers who rely on us to hunt for
              storage unit auctions happening near them
            </div>
          </section>
          <section className="mt-6 lg:mt-[2.125rem] mx-4.5 lg:mx-8 xl:mx-16 flex flex-col-reverse lg:flex-row lg:space-x-5">
            <div className="mt-7 lg:mt-0 lg:w-7/12 h-[37.5rem] lg:h-[48.375rem]">
              {mode && (
                <Map
                  mode={mode}
                  onChange={(e, p) => {
                    setItems(e);
                    setParams({ ...p, plan });
                  }}
                  setTotal={(e) => {
                    setTotalFac(e);
                  }}
                  onLoading={(e) => {
                    setLoading(e);
                  }}
                  getCity={(e) => {
                    setCity(e);
                  }}
                />
              )}
            </div>
            <div className="flex flex-col lg:w-5/12">
              <div className="text-neutral-900 font-bold text-title2 leading-[3rem] hidden lg:block">
                Storage Unit Auctions Found
              </div>
              <div className="mt-2 text-neutral-600 text-large-tight hidden lg:block">
                Found {totalFac} facilities and{" "}
                {items.data.found.upcoming_auction} auctions happening this week
              </div>
              {items.status === "NOTFOUND" ? (
                <div className="mt-6 lg:py-4.5 lg:px-6 flex flex-col">
                  <div className="bg-danger-50 rounded-full p-2.5 text-danger-500 w-fit">
                    <LocationCrossBoldIcon size="48" />
                  </div>
                  <div className="mt-6 flex flex-col">
                    <div className="text-primary-900 font-bold text-title3">
                      Subscription not available 😔
                    </div>
                    <div className="mt-3 text-neutral-900 text-xl leading-[1.875rem]">
                      Sorry, the selected location and radius have either no
                      storage facilities or low number of upcoming auctions
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mt-7 w-full rounded-full p-2 grid grid-cols-2 gap-x-5 bg-neutral-200">
                    <div
                      className={`${
                        plan === "monthly"
                          ? "bg-white font-bold text-primary-500"
                          : "bg-transparent font-medium text-neutral-500"
                      } w-full p-3 text-center flex items-center justify-center cursor-pointer text-regular-none rounded-full hover:bg-white hover:font-bold hover:text-primary-500 hover-anime`}
                      onClick={() => {
                        setPlan("monthly");
                        setParams({ ...params, plan: "monthly" });
                      }}
                    >
                      Monthly
                    </div>
                    <div
                      className={`${
                        plan === "yearly"
                          ? "bg-white font-bold text-primary-500"
                          : "bg-transparent font-medium text-neutral-500"
                      } w-full p-3 text-center flex items-center justify-center cursor-pointer text-regular-none rounded-full hover:bg-white hover:font-bold hover:text-primary-500 hover-anime`}
                      onClick={() => {
                        setPlan("yearly");
                        setParams({ ...params, plan: "yearly" });
                      }}
                    >
                      Yearly
                    </div>
                  </div>
                  {!loading && items.status ? (
                    <div className="mt-8 flex flex-col px-6 py-4.5 rounded-3xl border border-neutral-200">
                      <div className="font-bold text-primary-900 text-title2">
                        {plan === "yearly" ? "Yearly" : "Monthly"}
                      </div>
                      {plan === "yearly" ? (
                        <div className="mt-3 text-neutral-900 text-xl leading-[1.875rem]">
                          Save{" "}
                          {formatMoney(items.data.pricing.yearly_save_value)}
                          /year with a yearly plan
                        </div>
                      ) : (
                        <div className="mt-3 text-neutral-900 text-xl leading-[1.875rem]">
                          Sign up for a month to month plan
                        </div>
                      )}
                      <div className="mt-[1.875rem] font-bold text-info-500 text-title2">
                        {plan === "yearly"
                          ? formatMoney(items.data.pricing.yearly)
                          : formatMoney(items.data.pricing.monthly)}{" "}
                        <span className="font-normal text-neutral-900 text-regular-normal">
                          /{plan === "yearly" ? "Year" : "Month"}
                        </span>
                      </div>
                      <div className="mt-8 w-full">
                        <Link
                          href={
                            items.status === "NOTFOUND"
                              ? "#"
                              : `${process.env.NEXT_PUBLIC_BUYER_URL}/b/sign-up?lat=${params.lat}&lng=${params.lng}&radius=${params.radius}&plan=${params.plan}&address=${city}&map_zoom=${params.map_zoom}`
                          }
                        >
                          <a target="_blank">
                            <Button
                              size="lg"
                              className="!rounded-full w-full !font-medium"
                              onClick={() => {}}
                              disabled={items.status === "NOTFOUND"}
                            >
                              Subscribe Now
                            </Button>
                          </a>
                        </Link>
                      </div>
                      <div className="mt-6">
                        <div className="font-bold text-primary-900 text-title3">
                          This is what you get
                        </div>
                        <div className="mt-3 grid grid-cols-1 gap-y-3">
                          {what_you_got.map((e, key: number) => (
                            <section
                              key={key}
                              className="flex flex-row items-center space-x-3"
                            >
                              <div className="text-success-600">
                                <TickBulkCircleIcon />
                              </div>
                              <div className="text-xl leading-[1.875rem] text-neutral-900">
                                {e}
                              </div>
                            </section>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <LoadingPricingPlan />
                  )}
                </>
              )}
            </div>
          </section>

          <section className="mt-6 lg:hidden flex flex-col px-4.5">
            <div className="text-neutral-900 font-bold text-large-normal">
              Storage Unit Auctions Found
            </div>
            <div className="mt-2 text-neutral-700 text-small-normal pr-5">
              Found {items.data.found.facilities.length} auctions and {totalFac}{" "}
              of them happening this week
            </div>
          </section>

          {items.status !== "NOTFOUND" && (
            <section className="mt-4.5 lg:hidden relative group">
              <div className="absolute right-4.5 opacity-0 group-hover:opacity-100 hover-anime">
                <div className="bg-white rounded-full p-3 shadow-lv3">
                  <ArrowRight2Icon />
                </div>
              </div>
              <div className="px-4.5 overflow-x-auto sidebar flex flex-row items-center space-x-5">
                {items.data.found.facilities.map((e, key: number) => (
                  <CardAuction key={key} />
                ))}
              </div>
            </section>
          )}

          <section className={`${mode === "mobile" ? "gradient-main" : ""}`}>
            <div className="mt-12 md:mt-[7.75rem] mx-4.5 md:mx-8 xl:mx-16 md:pt-0 pt-11">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-[1.375rem] md:gap-x-5 md:gap-y-0 w-full">
                <AnimationOnScroll
                  animateIn="animate__fadeIn"
                  animateOnce
                  className="text-neutral-900 font-bold text-title3 leading-[2.125rem] md:text-title1 md:leading-[3.625rem] text-center md:text-left"
                >
                  The Leading{" "}
                  <span className="text-gradient-7">Storage Unit Auction</span>{" "}
                  Search Engine
                </AnimationOnScroll>
                <AnimationOnScroll
                  animateIn="animate__fadeIn"
                  animateOnce
                  className="text-neutral-700 md:text-neutral-900 text-small-normal leading-6 md:text-title3 md:leading-[2.125rem]  text-center md:text-left"
                >
                  Storage unit buyers from all over the country use Storage Unit
                  Auction list to find the most up to date auction listings.
                </AnimationOnScroll>
              </div>
            </div>
            <div className="mt-7 md:mt-[7.75rem] mx-4.5 md:mx-8 xl:mx-16 relative md:pt-[3.125rem] md:pb-11 flex flex-col items-center justify-center">
              <div className="absolute z-0 top-0 gradient-main rounded-[3rem] w-full md:w-[54.25rem] h-full md:h-[42.125rem] md:block hidden"></div>
              <div className="flex flex-col md:flex-wrap md:flex-row justify-center items-center md:gap-x-5 gap-y-3.5 relative w-full">
                {[...pricing_1, ...pricing_2].map((e, key: number) => (
                  <AnimationOnScroll
                    key={key}
                    className="w-full bg-white shadow-lv4 rounded-3xl p-6 flex flex-col items-center justify-center md:w-5/12 lg:w-[24rem] md:h-[19rem]"
                    animateIn="animate__zoomIn"
                    animateOnce
                  >
                    <Image
                      src={e.icon}
                      alt={e.title}
                      className="w-[5.875rem] h-[5.875rem]"
                    />
                    <div className="font-bold text-neutral-900 text-xl leading-[1.875rem] md:!text-title3 mt-3 md:mt-6 text-center">
                      {e.title}
                    </div>
                    <div className="text-small-normal md:text-large-normal md:text-neutral-900 mt-3 text-center">
                      {e.subtitle}
                    </div>
                  </AnimationOnScroll>
                ))}
              </div>
            </div>
          </section>
        </div>
      </DefaultLayout>
    </>
  );
};

export default Index;
